// Approve an un-vetted contractor — RON-5469. Prototype region: `#bd-approval`.
//
// ⚠ PROPOSED FLOW. Neither button has an endpoint behind it yet; see
// `docs/RON-5469_approval-flow-ask_19-Oct.md`. It is reachable only from the alert banner, which
// the live board never draws, so this dialog cannot be opened against the real server today.
//
// WHAT THIS SCREEN IS FOR. A contractor who is not fully vetted is standing at the kiosk. The
// kiosk has refused them and notified the host — the 16-October decision is that "a contractor
// never self-authorises". The exception is made here, by a named member of staff, against their
// login, and it is written to the audit trail.
//
// THERE IS DELIBERATELY NO PIN BOX. The spec puts a staff PIN on the KIOSK's own "Reception:
// approve entry" button, because that device stands unattended in a lobby. This screen is
// already behind a signed-in session and its own idle lock (§7b), so a second PIN would be
// theatre. The prototype's modal has none either.
//
// THE TWO GATES. Every outstanding item must be ticked and a reason chosen. They are not
// validation in the usual sense — nothing here is malformed without them. They exist so that
// admitting somebody is a series of deliberate acts rather than one press, and so the audit
// trail records WHAT the person was told and WHY they went ahead anyway.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  APPROVAL_REASONS,
  approvalReasonNeedsNote,
  canApproveEntry,
  formatApprovalSubject,
  getApprovalReasonLabelKey,
} from "../receptionConstants";

// Long enough for a sentence that will be read back in an audit months later, short enough that
// it is a reason and not an essay.
const NOTE_MAX = 500;

const ReceptionApprovalModal = ({ pending, isBusy, onApprove, onDeny, onClose }) => {
  const { t } = useTranslation("common");
  const [acknowledged, setAcknowledged] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  // A fresh dialog per contractor. Without this, the ticks and reason from the last person are
  // still set for the next one — and here that is the difference between a decision and a
  // leftover. Keyed on the visit rather than the object so a board poll that returns the same
  // person does not wipe a half-made decision.
  // Keyed on the APPROVAL id, not the visit: one visit can raise more than one request across a
  // day, and the board polls — so the same visit reappearing must not carry the last decision.
  useEffect(() => {
    setAcknowledged([]);
    setReason("");
    setNote("");
  }, [pending?.id]);

  if (!pending) return null;

  const toggle = (id) =>
    setAcknowledged((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );

  const outstanding = pending.outstanding ?? [];
  const needsNote = approvalReasonNeedsNote(reason);
  const canApprove = canApproveEntry({ outstanding, acknowledged, reason, note });

  const who = formatApprovalSubject(pending);

  return (
    <div className="reception-modal__backdrop" role="presentation" onClick={onClose}>
      <div
        className="reception-modal reception-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={t("react_reception_approval_title")}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="reception-modal__head">
          <h2>{t("react_reception_approval_title")}</h2>
          <button
            type="button"
            className="reception-modal__x"
            onClick={onClose}
            aria-label={t("react_reception_close")}
          >
            ✕
          </button>
        </div>

        <p className="reception-modal__sub">
          {t("react_reception_approval_sub").replace("%s", who)}
        </p>

        <div className="reception-modal__body">
          <p className="reception-field__label">
            {t("react_reception_approval_outstanding")}
          </p>

          {/* One tick per item, never one "I acknowledge all of the above". The list is short by
              nature and reading it item by item is the entire point of the gate. */}
          <ul className="reception-approval__items">
            {outstanding.map((item) => (
              <li key={item.id} className="reception-approval__item">
                <label>
                  <input
                    type="checkbox"
                    checked={acknowledged.includes(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                  <span>{item.label}</span>
                </label>
              </li>
            ))}
          </ul>

          <label className="reception-field">
            <span className="reception-field__label">
              {t("react_reception_approval_reason")}
            </span>
            <select
              className="reception-field__input"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            >
              <option value="">{t("react_reception_approval_reason_placeholder")}</option>
              {APPROVAL_REASONS.map((code) => (
                <option key={code} value={code}>
                  {t(getApprovalReasonLabelKey(code))}
                </option>
              ))}
            </select>
          </label>

          {/* ONLY for `other`, and REQUIRED there — their endpoint 422s without it.

              Until 22-October there was no note field at all, so a receptionist who chose
              "Other" could press Approve and be refused by the server with nothing on screen
              explaining why. */}
          {needsNote && (
            <label className="reception-field">
              <span className="reception-field__label">
                {t("react_reception_approval_note")}
              </span>
              <textarea
                className="reception-field__input reception-field__input--area"
                value={note}
                maxLength={NOTE_MAX}
                rows={3}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t("react_reception_approval_note_placeholder")}
              />
            </label>
          )}

          {/* Said BEFORE the press, not after. Everything in this sentence is a consequence the
              person approving is accountable for, and the one that matters most is the last:
              the contractor still counts on the evacuation muster. */}
          <p className="reception-approval__warn">
            {t("react_reception_approval_warning")}
          </p>
        </div>

        <div className="reception-modal__foot">
          {/* Deny sits on the LEFT, where cancel normally is, and it is not the dialog's cancel —
              closing with ✕ leaves the person waiting, denying them sends them away and tells
              the host. Two different outcomes, so the ✕ is kept as the way out that decides
              nothing. */}
          <button
            type="button"
            className="reception-btn reception-btn--ghost"
            disabled={isBusy}
            onClick={() => onDeny?.({ pending, reason, note })}
          >
            {t("react_reception_approval_deny")}
          </button>
          <button
            type="button"
            className="reception-btn"
            disabled={isBusy || !canApprove}
            onClick={() =>
              onApprove?.({
                pending,
                reason,
                note,
                // Sent as the ids the board gave us, not as the labels. The server writes an
                // audit row against document records, and English would not join back.
                //
                // It must be EXACTLY the current set, not merely cover it — their 422 is about
                // the list having changed underneath, and a subset passes a one-way check.
                acknowledged,
              })
            }
          >
            {t("react_reception_approval_approve")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceptionApprovalModal;
